/**
 * Internasjonale signalflagg (bokstavflagg) som SVG — brukt som klasseflagg.
 *
 * Forenklet, rektangulær fremstilling i et 90×60-lerret (svalehaler utelatt) for
 * tydelig og konsistent visning i appen. Hvert flagg har riktig farge/mønster.
 */
import type { ReactNode } from 'react';
import { FlagFrame } from './Flags.tsx';

const RED = '#cf2030';
const BLUE = '#0d4da8';
const YEL = '#f6d500';
const BLK = '#1b1b1b';
const WHT = '#ffffff';

const quartered = (a: string, b: string): ReactNode => (
  <>
    <rect width="45" height="30" fill={a} />
    <rect x="45" width="45" height="30" fill={b} />
    <rect y="30" width="45" height="30" fill={b} />
    <rect x="45" y="30" width="45" height="30" fill={a} />
  </>
);

const saltire = (bg: string, cross: string): ReactNode => (
  <>
    <rect width="90" height="60" fill={bg} />
    <line x1="0" y1="0" x2="90" y2="60" stroke={cross} strokeWidth="9" />
    <line x1="90" y1="0" x2="0" y2="60" stroke={cross} strokeWidth="9" />
  </>
);

const hBand = (top: string, mid: string): ReactNode => (
  <>
    <rect width="90" height="60" fill={top} />
    <rect y="20" width="90" height="20" fill={mid} />
  </>
);

const vHalves = (hoist: string, fly: string): ReactNode => (
  <>
    <rect width="45" height="60" fill={hoist} />
    <rect x="45" width="45" height="60" fill={fly} />
  </>
);

// SVG-innhold for hvert signalflagg i et 90×60-lerret.
const FLAGS: Record<string, { name: string; svg: ReactNode }> = {
  A: { name: 'Alfa', svg: vHalves(WHT, BLUE) },
  B: { name: 'Bravo', svg: <rect width="90" height="60" fill={RED} /> },
  C: {
    name: 'Charlie',
    svg: (
      <>
        {[BLUE, WHT, RED, WHT, BLUE].map((c, i) => (
          <rect key={i} y={i * 12} width="90" height="12" fill={c} />
        ))}
      </>
    ),
  },
  D: { name: 'Delta', svg: hBand(YEL, BLUE) },
  E: {
    name: 'Echo',
    svg: (
      <>
        <rect width="90" height="30" fill={BLUE} />
        <rect y="30" width="90" height="30" fill={RED} />
      </>
    ),
  },
  F: {
    name: 'Foxtrot',
    svg: (
      <>
        <rect width="90" height="60" fill={WHT} />
        <polygon points="45,8 72,30 45,52 18,30" fill={RED} />
      </>
    ),
  },
  G: {
    name: 'Golf',
    svg: (
      <>
        {[YEL, BLUE, YEL, BLUE, YEL, BLUE].map((c, i) => (
          <rect key={i} x={i * 15} width="15" height="60" fill={c} />
        ))}
      </>
    ),
  },
  H: { name: 'Hotel', svg: vHalves(WHT, RED) },
  I: {
    name: 'India',
    svg: (
      <>
        <rect width="90" height="60" fill={YEL} />
        <circle cx="45" cy="30" r="12" fill={BLK} />
      </>
    ),
  },
  J: { name: 'Juliett', svg: hBand(BLUE, WHT) },
  K: { name: 'Kilo', svg: vHalves(YEL, BLUE) },
  L: { name: 'Lima', svg: quartered(YEL, BLK) },
  M: { name: 'Mike', svg: saltire(BLUE, WHT) },
  N: {
    name: 'November',
    svg: (
      <>
        {Array.from({ length: 16 }, (_, i) => {
          const r = Math.floor(i / 4);
          const col = i % 4;
          return (
            <rect
              key={i}
              x={col * 22.5}
              y={r * 15}
              width="22.5"
              height="15"
              fill={(r + col) % 2 ? WHT : BLUE}
            />
          );
        })}
      </>
    ),
  },
  O: {
    name: 'Oscar',
    svg: (
      <>
        <polygon points="0,0 90,0 0,60" fill={RED} />
        <polygon points="90,0 90,60 0,60" fill={YEL} />
      </>
    ),
  },
  P: {
    name: 'Papa',
    svg: (
      <>
        <rect width="90" height="60" fill={BLUE} />
        <rect x="30" y="18" width="30" height="24" fill={WHT} />
      </>
    ),
  },
  Q: { name: 'Quebec', svg: <rect width="90" height="60" fill={YEL} /> },
  S: {
    name: 'Sierra',
    svg: (
      <>
        <rect width="90" height="60" fill={WHT} />
        <rect x="30" y="18" width="30" height="24" fill={BLUE} />
      </>
    ),
  },
  T: {
    name: 'Tango',
    svg: (
      <>
        <rect width="30" height="60" fill={RED} />
        <rect x="30" width="30" height="60" fill={WHT} />
        <rect x="60" width="30" height="60" fill={BLUE} />
      </>
    ),
  },
  U: { name: 'Uniform', svg: quartered(RED, WHT) },
  V: { name: 'Victor', svg: saltire(WHT, RED) },
  W: {
    name: 'Whiskey',
    svg: (
      <>
        <rect width="90" height="60" fill={BLUE} />
        <rect x="15" y="9" width="60" height="42" fill={WHT} />
        <rect x="30" y="21" width="30" height="18" fill={RED} />
      </>
    ),
  },
  X: {
    name: 'Xray',
    svg: (
      <>
        <rect width="90" height="60" fill={WHT} />
        <rect x="38" width="14" height="60" fill={BLUE} />
        <rect y="23" width="90" height="14" fill={BLUE} />
      </>
    ),
  },
};

export const FLAG_OPTIONS = Object.entries(FLAGS).map(([code, f]) => ({ code, name: f.name }));

/** Bare flagg-grafikken (uten ramme/animasjon). */
export function FlagGlyph({ code, className }: { code: string; className?: string }) {
  const f = FLAGS[code] ?? FLAGS.G!;
  return (
    <svg
      viewBox="0 0 90 60"
      className={className ?? 'w-20 h-14 md:w-28 md:h-20 drop-shadow-md'}
      role="img"
      aria-label={`Signalflagg ${code}`}
    >
      {f.svg}
      <rect x="0.75" y="0.75" width="88.5" height="58.5" fill="none" stroke="#111" strokeWidth="1.5" />
    </svg>
  );
}

/** Klasseflagg i startprosedyren — med opp/ned-animasjon og etikett. */
export function SignalFlag({ code, label, up }: { code: string; label: string; up: boolean }) {
  return (
    <FlagFrame up={up} label={label}>
      <FlagGlyph code={code} />
    </FlagFrame>
  );
}
